import React, { useState, useEffect } from 'react';
import {
  PRICE_COST_WITHOUT_VAT,
  PRICE_COST_WITHOUT_VAT_RUB,
  PRICE_RECOMMENDED_RETAIL,
  PRICE_RECOMMENDED_RETAIL_RUB,
} from '../constants/equipmentLabels';
import { equipment as equipmentApi, bookings as bookingsApi, auth } from '../api';
import { FiFilter, FiPackage, FiCalendar, FiGrid, FiList, FiSettings, FiHash, FiTruck, FiEdit2 } from 'react-icons/fi';
import EquipmentTable from '../components/EquipmentTable';
import EquipmentComment from '../components/EquipmentComment';
import { positionsLabel } from '../utils/pluralRu';
import { isNewItem } from '../utils/equipmentNew';

function Equipment() {
  const [equipmentList, setEquipmentList] = useState([]);
  const [user, setUser] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [equipmentTypes, setEquipmentTypes] = useState({ types: [], all: [] });
  const [selectedType, setSelectedType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(500);
  const [unavailableOnly, setUnavailableOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' или 'table'
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [bookingModal, setBookingModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [multipleBookingItems, setMultipleBookingItems] = useState([]);
  const [editing, setEditing] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    quantity: 1,
    start_date: '',
    end_date: '',
    client_name: '',
    deal_id: '',
    notes: ''
  });
  const [editForm, setEditForm] = useState({
    name: '',
    vendor: '',
    model: '',
    configuration: '',
    description: '',
    warehouse: '',
    location: '',
    comment: '',
    category: '',
    in_transit: '',
    price_ddp: '',
    price_gpl: '',
    price_retail: '',
    quantity: 1,
  });

  useEffect(() => {
    loadData();
    loadUser();
  }, [selectedVendor, selectedWarehouse, selectedType, rowsPerPage, unavailableOnly, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUser = async () => {
    try {
      const userData = await auth.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Error loading user:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [equipmentRes, vendorsRes, warehousesRes, typesRes] = await Promise.all([
        equipmentApi.getAll({
          vendor: selectedVendor || undefined,
          warehouse: selectedWarehouse || undefined,
          category: selectedType || undefined,
          q: searchQuery || undefined,
          limit: rowsPerPage,
          available_only: !unavailableOnly,
          unavailable_only: unavailableOnly
        }),
        equipmentApi.getVendors(),
        equipmentApi.getWarehouses(),
        // Фильтр по типу не должен ронять страницу, если бэкенд ещё старый.
        equipmentApi
          .getTypes({ available_only: !unavailableOnly, unavailable_only: unavailableOnly })
          .catch(() => ({ types: [], all: [] })),
      ]);
      setEquipmentList(equipmentRes);
      setVendors(vendorsRes);
      setWarehouses(warehousesRes);
      setEquipmentTypes(typesRes);
    } catch (error) {
      console.error('Error loading equipment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    try {
      // Создаем бронирования для всех выбранных позиций
      const bookingPromises = multipleBookingItems.map(item => 
        bookingsApi.create({
          equipment_id: item.id,
          quantity: bookingForm.quantity,
          start_date: new Date(bookingForm.start_date).toISOString(),
          end_date: new Date(bookingForm.end_date).toISOString(),
          client_name: bookingForm.client_name,
          deal_id: bookingForm.deal_id || undefined,
          notes: bookingForm.notes
        })
      );
      
      await Promise.all(bookingPromises);
      
      const message = multipleBookingItems.length > 1 
        ? `Успешно забронировано ${multipleBookingItems.length} ${positionsLabel(multipleBookingItems.length)}!`
        : 'Бронирование успешно создано!';
      
      alert(message);
      setBookingModal(null);
      setMultipleBookingItems([]);
      setSelectedCards(new Set()); // Снимаем выделение после бронирования
      setBookingForm({
        quantity: 1,
        start_date: '',
        end_date: '',
        client_name: '',
        deal_id: '',
        notes: ''
      });
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Ошибка создания бронирования');
    }
  };

  const toggleCardSelection = (itemId) => {
    const newSelected = new Set(selectedCards);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedCards(newSelected);
  };

  const toggleSelectAllCards = () => {
    if (selectedCards.size === equipmentList.length) {
      setSelectedCards(new Set());
    } else {
      setSelectedCards(new Set(equipmentList.map(item => item.id)));
    }
  };

  const bookSelectedItems = () => {
    const items = equipmentList.filter(item => selectedCards.has(item.id));
    if (items.length > 0) {
      // Для множественного бронирования используем первый элемент как шаблон
      openBookingModal(items[0], items);
    }
  };

  const openBookingModal = (item, allItems = null) => {
    setBookingModal(item);
    setMultipleBookingItems(allItems || [item]);
    
    // Вычисляем минимальную дату начала резерва
    let minDate = new Date();
    minDate.setDate(minDate.getDate() + 1);
    
    // Если товар в транзите, вычисляем дату после транзита
    if (item.in_transit) {
      try {
        const transitParts = item.in_transit.split('.');
        if (transitParts.length === 2) {
          const transitMonth = parseInt(transitParts[0]);
          const transitYear = parseInt(transitParts[1]);
          // Первый день следующего месяца после транзита
          // В JS месяцы начинаются с 0, поэтому для следующего месяца используем transitMonth (не transitMonth-1)
          let nextMonth = transitMonth; // Это уже следующий месяц (11.2025 -> декабрь 2025)
          let nextYear = transitYear;
          if (nextMonth === 12) {
            nextMonth = 1;
            nextYear += 1;
          }
          const transitDate = new Date(nextYear, nextMonth - 1, 1); // -1 потому что в JS месяцы с 0
          if (transitDate > minDate) {
            minDate = transitDate;
          }
        }
      } catch (e) {
        console.error('Ошибка парсинга даты транзита:', e);
      }
    }
    
    const nextWeek = new Date(minDate);
    nextWeek.setDate(nextWeek.getDate() + 7);
    
    setBookingForm({
      quantity: 1,
      start_date: minDate.toISOString().split('T')[0],
      end_date: nextWeek.toISOString().split('T')[0],
      client_name: '',
      deal_id: '',
      notes: ''
    });
  };

  const openEditModal = (item) => {
    setEditModal(item);
    setEditForm({
      name: item.name || '',
      vendor: item.vendor || '',
      model: item.model || '',
      configuration: item.configuration || '',
      description: item.description || '',
      warehouse: item.warehouse || '',
      location: item.location || '',
      comment: item.comment || '',
      // Пусто — тип по названию; значение — тип, который координатор выбрал сам.
      category: item.category_manual ? item.category || '' : '',
      in_transit: item.in_transit || '',
      price_ddp: item.price_ddp ?? '',
      price_gpl: item.price_gpl ?? '',
      price_retail: item.price_retail ?? '',
      quantity: item.quantity || 1,
    });
  };

  const handleEditEquipment = async (e) => {
    e.preventDefault();
    if (!editModal) return;

    setEditing(true);
    try {
      await equipmentApi.update(editModal.id, {
        ...editForm,
        quantity: parseInt(editForm.quantity, 10) || 0,
        price_ddp: editForm.price_ddp === '' ? null : parseFloat(editForm.price_ddp),
        price_gpl: editForm.price_gpl === '' ? null : parseFloat(editForm.price_gpl),
        price_retail: editForm.price_retail === '' ? null : parseFloat(editForm.price_retail),
        in_transit: editForm.in_transit || null,
      });
      setEditModal(null);
      await loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Ошибка при обновлении товара');
    } finally {
      setEditing(false);
    }
  };

  return (
    <div className="px-2 sm:px-4 lg:px-6 xl:px-8">
      <div className="mb-4 sm:mb-6 lg:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Оборудование на складе</h1>
        <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-600">
          Просмотр и бронирование доступного оборудования
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white p-3 sm:p-4 rounded-lg shadow mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-2">
            <FiFilter className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Фильтры:</span>
          </div>
          
          <select
            value={selectedVendor}
            onChange={(e) => setSelectedVendor(e.target.value)}
            className="flex-1 sm:flex-none border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Все вендоры</option>
            {vendors.map((vendor) => (
              <option key={vendor} value={vendor}>
                {vendor}
              </option>
            ))}
          </select>

          <select
            value={selectedWarehouse}
            onChange={(e) => setSelectedWarehouse(e.target.value)}
            className="flex-1 sm:flex-none border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Все склады</option>
            {warehouses.map((warehouse) => (
              <option key={warehouse} value={warehouse}>
                {warehouse}
              </option>
            ))}
          </select>

          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            aria-label="Тип оборудования"
            className="flex-1 sm:flex-none border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Все типы</option>
            {equipmentTypes.types.map((type) => (
              <option key={type.name} value={type.name}>
                {type.name} ({type.count})
              </option>
            ))}
          </select>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={unavailableOnly}
              onChange={(e) => setUnavailableOnly(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Только недоступные для резервирования</span>
          </label>

          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Поиск по всем полям..."
            className="flex-1 min-w-[220px] border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700 whitespace-nowrap">Строк на экране:</span>
            <select
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
            </select>
          </div>

          {/* View mode toggle - только для desktop */}
          <div className="hidden lg:flex items-center border border-gray-300 rounded-lg">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 text-sm font-medium rounded-l-lg transition-colors ${
                viewMode === 'grid'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FiGrid className="inline mr-1" />
              Карточки
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 text-sm font-medium rounded-r-lg transition-colors ${
                viewMode === 'table'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FiList className="inline mr-1" />
              Таблица
            </button>
          </div>

          {viewMode === 'grid' && equipmentList.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={toggleSelectAllCards}
                className="px-3 py-2 text-sm font-medium border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                {selectedCards.size === equipmentList.length ? 'Снять выделение' : 'Выбрать все'}
              </button>
              
              {selectedCards.size > 0 && (
                <button
                  onClick={bookSelectedItems}
                  className="inline-flex items-center px-3 py-2 border border-blue-300 shadow-sm text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50"
                >
                  <FiCalendar className="mr-2" />
                  Забронировать ({selectedCards.size})
                </button>
              )}
            </div>
          )}

          <div className="text-sm text-gray-600 sm:ml-auto">
            Найдено: <strong>{equipmentList.length}</strong> {positionsLabel(equipmentList.length)}
          </div>
        </div>
      </div>

      {/* Equipment Display */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-xl text-gray-600">Загрузка...</div>
        </div>
      ) : equipmentList.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <FiPackage className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Оборудование не найдено</h3>
          <p className="mt-1 text-sm text-gray-500">
            Попробуйте изменить фильтры
          </p>
        </div>
      ) : viewMode === 'table' ? (
        <EquipmentTable 
          equipmentList={equipmentList} 
          onBook={openBookingModal}
          selectedItems={selectedCards}
          setSelectedItems={setSelectedCards}
          canEdit={Boolean(user?.is_admin)}
          onEdit={openEditModal}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {equipmentList.map((item) => (
            <div
              key={item.id}
              className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden ${
                selectedCards.has(item.id) ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <input
                      type="checkbox"
                      checked={selectedCards.has(item.id)}
                      onChange={() => toggleCardSelection(item.id)}
                      className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {item.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {item.vendor} {item.model && `• ${item.model}`}
                      </p>
                      {item.category && (
                        <p className="text-xs text-gray-500 mt-0.5">{item.category}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                  {isNewItem(item) && (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      Новое
                    </span>
                  )}
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      item.available_quantity > 0
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {item.available_quantity > 0 ? 'Доступно' : 'Нет в наличии'}
                  </span>
                    {item.in_transit && (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 flex items-center gap-1">
                        <FiTruck className="h-3 w-3" />
                        В транзите до {item.in_transit}
                      </span>
                    )}
                  </div>
                </div>

                <EquipmentComment text={item.comment} className="mt-3" />

                {item.description && (
                  <p className="mt-3 text-sm text-gray-600 line-clamp-2">
                    {item.description}
                  </p>
                )}

                <div className="mt-4 space-y-2">
                  {item.warehouse && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium">Склад:</span>
                      <span className="ml-2">{item.warehouse}</span>
                    </div>
                  )}
                  {item.configuration && (
                    <div className="flex items-center text-sm text-gray-600">
                      <FiSettings className="mr-2 h-4 w-4" />
                      <span className="truncate" title={item.configuration}>
                        {item.configuration}
                      </span>
                    </div>
                  )}
                  {item.price_ddp && (
                    <div className="flex items-center text-sm font-medium text-gray-900">
                      {PRICE_COST_WITHOUT_VAT}: {item.price_ddp.toLocaleString('ru-RU')} ₽
                    </div>
                  )}
                  {item.price_gpl && (
                    <div className="flex items-center text-sm font-medium text-gray-900">
                      Цена GPL: {item.price_gpl.toLocaleString('ru-RU')} CNY
                    </div>
                  )}
                  {item.price_retail != null && item.price_retail !== '' && (
                    <div className="flex items-center text-sm font-medium text-gray-900">
                      {PRICE_RECOMMENDED_RETAIL}: {item.price_retail.toLocaleString('ru-RU')} ₽
                    </div>
                  )}
                  {item.code_1c && (
                    <div className="flex items-center text-sm text-gray-600">
                      <FiHash className="mr-2 h-4 w-4" />
                      <span className="font-mono text-xs">{item.code_1c}</span>
                    </div>
                  )}
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="font-medium">Доступно:</span>
                    <span className="ml-2">
                      {item.available_quantity} из {item.quantity} шт
                    </span>
                  </div>
                  {item.in_transit && (
                    <div className="flex items-center text-sm text-yellow-700 bg-yellow-50 px-2 py-1 rounded mt-2">
                      <FiTruck className="mr-2 h-4 w-4" />
                      <span className="font-medium">В транзите до {item.in_transit}</span>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => openBookingModal(item)}
                  disabled={item.available_quantity === 0}
                  className="mt-4 w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <FiCalendar className="mr-2" />
                  Забронировать
                </button>
                <button
                  onClick={() => openEditModal(item)}
                  disabled={!user?.is_admin}
                  title={user?.is_admin ? 'Редактировать карточку товара' : 'Редактирование доступно только администратору'}
                  className="mt-2 w-full flex items-center justify-center px-4 py-2 border border-amber-300 text-sm font-medium rounded-lg text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiEdit2 className="mr-2" />
                  {user?.is_admin ? 'Редактировать карточку' : 'Редактирование (админ)'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Equipment Modal */}
      {editModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-start justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 my-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Редактирование карточки товара
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Код 1С: <span className="font-mono">{editModal.code_1c || '-'}</span> (только чтение)
            </p>
            <form onSubmit={handleEditEquipment} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input className="border rounded-lg px-3 py-2" placeholder="Наименование" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required />
              <input className="border rounded-lg px-3 py-2" placeholder="Вендор" value={editForm.vendor} onChange={(e) => setEditForm({ ...editForm, vendor: e.target.value })} required />
              <input className="border rounded-lg px-3 py-2" placeholder="Модель" value={editForm.model} onChange={(e) => setEditForm({ ...editForm, model: e.target.value })} />
              <input className="border rounded-lg px-3 py-2" placeholder="Конфигурация" value={editForm.configuration} onChange={(e) => setEditForm({ ...editForm, configuration: e.target.value })} />
              <input className="border rounded-lg px-3 py-2" placeholder="Склад" value={editForm.warehouse} onChange={(e) => setEditForm({ ...editForm, warehouse: e.target.value })} />
              <input className="border rounded-lg px-3 py-2" placeholder="Место" value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} />
              <select
                className="border rounded-lg px-3 py-2"
                aria-label="Тип оборудования"
                value={editForm.category}
                onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
              >
                <option value="">
                  {editModal.category && !editModal.category_manual
                    ? `Тип по названию: ${editModal.category}`
                    : 'Тип — определить по названию'}
                </option>
                {equipmentTypes.all.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <input className="border rounded-lg px-3 py-2" placeholder="В транзите (MM.YYYY)" value={editForm.in_transit} onChange={(e) => setEditForm({ ...editForm, in_transit: e.target.value })} />
              <input className="border rounded-lg px-3 py-2" type="number" min="0" placeholder="Количество" value={editForm.quantity} onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })} />
              <input className="border rounded-lg px-3 py-2" type="number" step="0.01" placeholder={PRICE_COST_WITHOUT_VAT_RUB} value={editForm.price_ddp} onChange={(e) => setEditForm({ ...editForm, price_ddp: e.target.value })} />
              <input className="border rounded-lg px-3 py-2" type="number" step="0.01" placeholder={PRICE_RECOMMENDED_RETAIL_RUB} value={editForm.price_retail} onChange={(e) => setEditForm({ ...editForm, price_retail: e.target.value })} />
              <input className="border rounded-lg px-3 py-2 md:col-span-2" type="number" step="0.01" placeholder="Цена GPL" value={editForm.price_gpl} onChange={(e) => setEditForm({ ...editForm, price_gpl: e.target.value })} />
              <textarea className="border rounded-lg px-3 py-2 md:col-span-2" rows="3" placeholder="Описание" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
              <textarea className="border rounded-lg px-3 py-2 md:col-span-2" rows="3" placeholder="Комментарий" value={editForm.comment} onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })} />

              <div className="md:col-span-2 flex gap-3 pt-2">
                <button type="button" onClick={() => setEditModal(null)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg">Отмена</button>
                <button type="submit" disabled={editing} className="flex-1 px-4 py-2 text-white bg-primary-600 rounded-lg disabled:opacity-50">
                  {editing ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {bookingModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-start justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 my-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Бронирование оборудования
            </h2>
            
            {multipleBookingItems.length > 1 ? (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="font-medium text-gray-900 mb-2">
                  Выбрано {multipleBookingItems.length} {positionsLabel(multipleBookingItems.length)}
                </p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {multipleBookingItems.map((item, idx) => (
                    <p key={idx} className="text-sm text-gray-700">
                      • {item.vendor} {item.model}
                      {item.code_1c && (
                        <span className="ml-1 font-mono text-gray-500">({item.code_1c})</span>
                      )}
                      <EquipmentComment text={item.comment} compact className="ml-3 mt-0.5" />
                      {item.price_ddp != null && item.price_ddp !== '' && (
                        <span className="block ml-3 text-xs text-gray-700 mt-0.5">
                          {PRICE_COST_WITHOUT_VAT}: {Number(item.price_ddp).toLocaleString('ru-RU')} ₽
                        </span>
                      )}
                    </p>
                  ))}
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Одно бронирование будет создано для всех позиций с одинаковыми параметрами
                </p>
              </div>
            ) : (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-900">{bookingModal.name}</p>
                <p className="text-sm text-gray-600">
                  {bookingModal.vendor} {bookingModal.model}
                </p>
                <EquipmentComment text={bookingModal.comment} className="mt-2" />
                {bookingModal.code_1c && (
                  <p className="text-sm text-gray-600 mt-1 flex items-center">
                    <FiHash className="mr-1 h-3.5 w-3.5" />
                    <span className="font-mono">{bookingModal.code_1c}</span>
                  </p>
                )}
                {bookingModal.price_ddp != null && bookingModal.price_ddp !== '' && (
                  <p className="text-sm text-gray-900 mt-1">
                    <strong>{PRICE_COST_WITHOUT_VAT}:</strong>{' '}
                    {Number(bookingModal.price_ddp).toLocaleString('ru-RU')} ₽
                  </p>
                )}
                {bookingModal.in_transit && (
                  <div className="mt-2 flex items-center text-sm text-yellow-700 bg-yellow-50 px-2 py-1 rounded">
                    <FiTruck className="mr-2 h-4 w-4" />
                    <span>В транзите до {bookingModal.in_transit}. Резервирование возможно только после этой даты.</span>
                  </div>
                )}
              </div>
            )}
            <form onSubmit={handleBooking} className="space-y-4">
              {multipleBookingItems.length === 1 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Количество (макс. {bookingModal.available_quantity})
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={bookingModal.available_quantity}
                    value={bookingForm.quantity}
                    onChange={(e) =>
                      setBookingForm({ ...bookingForm, quantity: parseInt(e.target.value) })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Дата начала
                  {bookingModal.in_transit && (
                    <span className="text-xs text-yellow-700 ml-2">
                      (не ранее {(() => {
                        try {
                          const transitParts = bookingModal.in_transit.split('.');
                          if (transitParts.length === 2) {
                            const transitMonth = parseInt(transitParts[0]);
                            const transitYear = parseInt(transitParts[1]);
                            let nextMonth = transitMonth;
                            let nextYear = transitYear;
                            if (nextMonth === 12) {
                              nextMonth = 1;
                              nextYear += 1;
                            }
                            const transitDate = new Date(nextYear, nextMonth - 1, 1);
                            return transitDate.toLocaleDateString('ru-RU');
                          }
                        } catch (e) {}
                        return bookingModal.in_transit;
                      })()})
                    </span>
                  )}
                </label>
                <input
                  type="date"
                  value={bookingForm.start_date}
                  onChange={(e) =>
                    setBookingForm({ ...bookingForm, start_date: e.target.value })
                  }
                  min={(() => {
                    if (bookingModal.in_transit) {
                      try {
                        const transitParts = bookingModal.in_transit.split('.');
                        if (transitParts.length === 2) {
                          const transitMonth = parseInt(transitParts[0]);
                          const transitYear = parseInt(transitParts[1]);
                          let nextMonth = transitMonth;
                          let nextYear = transitYear;
                          if (nextMonth === 12) {
                            nextMonth = 1;
                            nextYear += 1;
                          }
                          const transitDate = new Date(nextYear, nextMonth - 1, 1);
                          return transitDate.toISOString().split('T')[0];
                        }
                      } catch (e) {}
                    }
                    return new Date().toISOString().split('T')[0];
                  })()}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Дата окончания
                </label>
                <input
                  type="date"
                  value={bookingForm.end_date}
                  onChange={(e) =>
                    setBookingForm({ ...bookingForm, end_date: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Имя клиента
                </label>
                <input
                  type="text"
                  value={bookingForm.client_name}
                  onChange={(e) =>
                    setBookingForm({ ...bookingForm, client_name: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Для какого клиента бронируется"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ID сделки Bitrix24
                </label>
                <input
                  type="text"
                  value={bookingForm.deal_id}
                  onChange={(e) =>
                    setBookingForm({ ...bookingForm, deal_id: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Например: 12345 или ссылка на сделку"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Примечания
                </label>
                <textarea
                  value={bookingForm.notes}
                  onChange={(e) =>
                    setBookingForm({ ...bookingForm, notes: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows="3"
                  placeholder="Дополнительная информация"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setBookingModal(null);
                    setMultipleBookingItems([]);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                >
                  {multipleBookingItems.length > 1 
                    ? `Забронировать все (${multipleBookingItems.length})`
                    : 'Забронировать'
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Equipment;

